import React, { useState, useRef } from 'react';
import { uploadToImgbb } from '../utils/imgbb';

const CastForm = ({ onAdd, existingCasts = {} }) => {
  const [open,         setOpen]         = useState(false);
  const [vrchatName,   setVrchatName]   = useState('');
  const [vrchatProfile, setVrchatProfile] = useState('');
  const [xUrl,         setXUrl]         = useState('');
  const [imageUrl,     setImageUrl]     = useState('');
  const [dupError,     setDupError]     = useState(false);
  const [imgStatus,    setImgStatus]    = useState('idle'); // idle | uploading | done | error
  const [imgErr,       setImgErr]       = useState('');
  const fileInputRef = useRef(null);

  const isDuplicate = (name) =>
    Object.values(existingCasts).some(
      c => (c.vrchat_name || c.name || '').trim().toLowerCase() === name.trim().toLowerCase()
    );

  const handleNameChange = (e) => {
    const v = e.target.value;
    setVrchatName(v);
    setDupError(v.trim() ? isDuplicate(v) : false);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImgErr('画像ファイルを選択してください'); return; }
    if (file.size > 32 * 1024 * 1024) { setImgErr('32MB以下の画像を選択してください'); return; }
    setImgStatus('uploading');
    setImgErr('');
    try {
      const url = await uploadToImgbb(file);
      setImageUrl(url);
      setImgStatus('done');
    } catch (err) {
      setImgErr(err.message || 'アップロードに失敗しました');
      setImgStatus('error');
    }
    e.target.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!vrchatName.trim() || dupError || imgStatus === 'uploading') return;
    onAdd({
      vrchat_name:    vrchatName.trim(),
      vrchat_profile: vrchatProfile.trim() || null,
      x_url:          xUrl.trim()          || null,
      image_url:      imageUrl             || null,
    });
    reset();
  };

  const reset = () => {
    setVrchatName(''); setVrchatProfile(''); setXUrl(''); setImageUrl('');
    setDupError(false); setImgStatus('idle'); setImgErr('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button style={styles.addButton} onClick={() => setOpen(true)}>
        ＋ キャスト追加
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={styles.container}>
      {/* VRChat名（必須） */}
      <div>
        <label style={styles.label}>VRChat名 <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          style={{ ...styles.input, borderColor: dupError ? '#ef4444' : '#d1d5db' }}
          value={vrchatName}
          onChange={handleNameChange}
          placeholder="VRChat名（イベント内で一意）"
          autoFocus
        />
        {dupError && <span style={styles.errorMsg}>⚠️ 同じVRChat名がすでに登録されています</span>}
      </div>

      {/* プロフィール画像（任意） */}
      <div>
        <label style={styles.label}>プロフィール画像 <span style={styles.optional}>任意</span></label>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageSelect} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: 52, height: 52, borderRadius: '50%', cursor: 'pointer',
              border: '2px dashed #c4b5fd', overflow: 'hidden', flexShrink: 0,
              background: imageUrl ? 'transparent' : 'linear-gradient(135deg, #6c63ff, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {imageUrl
              ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: 20, color: '#fff' }}>📷</span>
            }
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={imgStatus === 'uploading'}
              style={{ ...styles.subBtn, opacity: imgStatus === 'uploading' ? 0.6 : 1 }}
            >
              {imgStatus === 'uploading' ? 'アップロード中...' : imgStatus === 'done' ? '✓ 画像を変更' : '画像を選択'}
            </button>
            {imageUrl && (
              <button type="button" onClick={() => { setImageUrl(''); setImgStatus('idle'); }}
                style={{ ...styles.subBtn, color: '#ef4444', borderColor: '#fca5a5' }}>
                画像を削除
              </button>
            )}
            {imgErr && <span style={styles.errorMsg}>{imgErr}</span>}
          </div>
        </div>
      </div>

      {/* VRChatプロフィールURL（任意） */}
      <div>
        <label style={styles.label}>VRChatプロフィールURL <span style={styles.optional}>任意</span></label>
        <input
          style={styles.input}
          value={vrchatProfile}
          onChange={e => setVrchatProfile(e.target.value)}
          placeholder="https://vrchat.com/home/user/usr_..."
        />
      </div>

      {/* X URL（任意） */}
      <div>
        <label style={styles.label}>X (Twitter) URL <span style={styles.optional}>任意</span></label>
        <input
          style={styles.input}
          value={xUrl}
          onChange={e => setXUrl(e.target.value)}
          placeholder="https://x.com/yourname"
        />
      </div>

      <div style={styles.actions}>
        <button
          type="submit"
          style={{
            ...styles.submitButton,
            opacity: (!vrchatName.trim() || dupError || imgStatus === 'uploading') ? 0.5 : 1,
            cursor: (!vrchatName.trim() || dupError || imgStatus === 'uploading') ? 'not-allowed' : 'pointer',
          }}
          disabled={!vrchatName.trim() || dupError || imgStatus === 'uploading'}
        >
          追加
        </button>
        <button type="button" style={styles.cancelButton} onClick={reset}>
          キャンセル
        </button>
      </div>
    </form>
  );
};

const styles = {
  addButton: {
    width: '100%',
    border: '2px dashed #c7d2fe',
    background: '#eef2ff',
    color: '#4338ca',
    padding: '14px',
    borderRadius: '16px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '15px',
    transition: '0.2s',
    marginBottom: '20px',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#334155',
    marginBottom: 4,
  },
  optional: {
    marginLeft: 6,
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 400,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1.5px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  subBtn: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  errorMsg: {
    display: 'block',
    marginTop: 4,
    fontSize: '12px',
    color: '#ef4444',
    fontWeight: '600',
  },
  actions: { display: 'flex', gap: '10px' },
  submitButton: {
    flex: 1,
    background: '#6c63ff',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '12px',
    fontWeight: '700',
    fontSize: '14px',
    transition: 'opacity 0.15s',
  },
  cancelButton: {
    flex: 1,
    background: '#fff',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '12px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '14px',
  },
};

export default CastForm;
